import React, { PureComponent, Suspense } from 'react';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import widgetConfig from '../../configs/WidgetConfig';
import { REDUCER_NAME } from '../app/commonReducer';
import store from '../app/store';
import { CURRENT_STATE_KEY } from '../../configs/LayoutConfig';

import authProvider from '../../core/AuthenticationProvider';
import { setUserInfo } from '../app/reducer';
import ConstCommon from '../common/Const';
import currentState from '../helpers/CurrentState';
import userSettingService from '../../core/services/Common/UserSettingService';
import { formatDate } from '../helpers/FormatDate';

const ConsensusAnalysis = React.lazy(() =>
  import('../fundamental/consensus-analysis/ConsensusAnalysis'),
);
const FADividendAnalysis = React.lazy(() =>
  import('../fundamental/dividend-analysis/FADividendAnalysis'),
);
const FinancialAnalysis = React.lazy(() =>
  import('../fundamental/financial-analysis'),
);
const FinancialRatio = React.lazy(() =>
  import('../fundamental/financial-ratio'),
);
const FinancialStatement = React.lazy(() =>
  import('../fundamental/financial-statement/FinancialStatement'),
);
const Ownership = React.lazy(() =>
  import('../fundamental/ownership/Ownership'),
);
const FASnapshot = React.lazy(() =>
  import('../fundamental/snapshot/FASnapshot'),
);
const HeatMap = React.lazy(() => import('../market/heatmap/HeatMap'));
const MarketCalendar = React.lazy(() =>
  import('../market/market-calendar/MarketCalendar'),
);
const MoneyFlow = React.lazy(() => import('../market/money-flow/MoneyFlow'));
const TopBreakout = React.lazy(() =>
  import('../market/top-mover/top-breakout'),
);
const TopForeignTrade = React.lazy(() =>
  import('../market/top-mover/top-foreign-trading'),
);
const TopGainers = React.lazy(() => import('../market/top-mover/top-gainers'));
const TopLosers = React.lazy(() => import('../market/top-mover/top-losers'));
const TopNewHigh = React.lazy(() => import('../market/top-mover/top-new-high'));
const TopNewLow = React.lazy(() => import('../market/top-mover/top-new-low'));
const TopValue = React.lazy(() => import('../market/top-mover/top-value'));
const TopVolume = React.lazy(() => import('../market/top-mover/top-volume'));
const WatchList = React.lazy(() => import('../market/watchlist/WatchList'));
const News = React.lazy(() => import('../news-and-analysis/News'));
const Order = React.lazy(() => import('../order/Order'));
const PriceData = React.lazy(() => import('../prices/price-data/PriceData'));
const PriceDepth = React.lazy(() => import('../prices/price-depth/PriceDepth'));
const TimeAndSale = React.lazy(() =>
  import('../prices/time-and-sales/TimeAndSale'),
);
const STRanking = React.lazy(() => import('../strategy/ranking/STRanking'));
const Strategy = React.lazy(() => import('../strategy/strategy/Strategy'));
const TASignals = React.lazy(() => import('../technical/ta-signals/TASignals'));
const Alerts = React.lazy(() => import('../tools/alerts'));
const Screener = React.lazy(() => import('../tools/screener/Screener'));
const TradeData = React.lazy(() => import('../tools/trade-data/TradeData'));
const Valuation = React.lazy(() => import('../tools/valuation/Valuation'));
const MarketIndepth = React.lazy(() => import('../market/market-in-depth'));

const DefaultComponent = () => null;

const mapComponent = {
  'market-indepth': MarketIndepth,
  watchlist: WatchList,
  'top-volume': TopVolume,
  'top-breakout': TopBreakout,
  'top-value': TopValue,
  'top-gainers': TopGainers,
  'top-losers': TopLosers,
  'top-new-high': TopNewHigh,
  'top-new-low': TopNewLow,
  'top-foreign': TopForeignTrade,
  'market-calendar': MarketCalendar,
  heatmap: HeatMap,
  'money-flow': MoneyFlow,
  timensales: TimeAndSale,
  'price-depth': PriceDepth,
  'price-data': PriceData,
  'technical-analysis': TASignals,
  news: News,
  snapshot: FASnapshot,
  ownership: Ownership,
  'financial-analysis': FinancialAnalysis,
  'financial-ratio': FinancialRatio,
  'cash-dividend-analysis': FADividendAnalysis,
  'consensus-analysis': ConsensusAnalysis,
  'financial-statement': FinancialStatement,
  alerts: Alerts,
  screener: Screener,
  valuation: Valuation,
  fiinta: TradeData,
  ranking: STRanking,
  strategy: Strategy,
  order: Order,
};

const mapComponentName = {
  'market-indepth': ConstCommon.listComponent.MarketInDepth,
  watchlist: ConstCommon.listComponent.Watchlist,
  'top-volume': ConstCommon.listComponent.TopVolume,
  'top-breakout': ConstCommon.listComponent.TopBreakout,
  'top-value': ConstCommon.listComponent.TopValue,
  'top-gainers': ConstCommon.listComponent.TopGainers,
  'top-losers': ConstCommon.listComponent.TopLosers,
  'top-new-high': ConstCommon.listComponent.TopNewHigh,
  'top-new-low': ConstCommon.listComponent.TopNewLow,
  'top-foreign': ConstCommon.listComponent.TopForeignTrade,
  'market-calendar': ConstCommon.listComponent.MarketCalendar,
  heatmap: ConstCommon.listComponent.HeatMap,
  'money-flow': ConstCommon.listComponent.MoneyFlow,
  timensales: ConstCommon.listComponent.TimeAndSales,
  'price-depth': ConstCommon.listComponent.PriceDepth,
  'price-data': ConstCommon.listComponent.PriceData,
  'technical-analysis': ConstCommon.listComponent.TASignals,
  news: ConstCommon.listComponent.News,
  snapshot: ConstCommon.listComponent.FASnapshot,
  ownership: ConstCommon.listComponent.Ownership,
  'financial-analysis': ConstCommon.listComponent.FinancialAnalysis,
  'financial-ratio': ConstCommon.listComponent.FinancialRatio,
  'cash-dividend-analysis': ConstCommon.listComponent.FADividendAnalysis,
  'consensus-analysis': ConstCommon.listComponent.ConsensusAnalysis,
  'financial-statement': ConstCommon.listComponent.FinancialStatement,
  alerts: ConstCommon.listComponent.Alerts,
  screener: ConstCommon.listComponent.Screener,
  valuation: ConstCommon.listComponent.Valuation,
  fiinta: ConstCommon.listComponent.TradeData,
  ranking: ConstCommon.listComponent.STRanking,
  strategy: ConstCommon.listComponent.Strategy,
  order: 'Order',
};

const defaultComponentId = 1;
const customProps = { id: defaultComponentId };

const objectLength = obj => !!Object.keys(obj).length;

class Component extends PureComponent {
  constructor(props) {
    super(props);
    const { setUserInfo } = this.props;
    this.loadCurrentState();
    this.initComponent();
    this.rootRef = React.createRef();
    setUserInfo({ name: 'whatever' }); //Fake login
  }

  componentDidMount() {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    this.getChangeLogs();
    this.changeTitle();
    this.calcSize();
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  }

  getChangeLogs = () => {
    const isNotPopout = document.getElementById('root');
    if (isNotPopout) {
      var lastVersion = window.localStorage.getItem('fversion');
      if (!lastVersion) {
        lastVersion = '0.0.0.1';
      }

      userSettingService
        .getChangeLogsV3({ lastVersion: lastVersion })
        .then(({ items }) => {
          if (!items || (Array.isArray(items) && !items.length)) {
            return;
          }

          navigator.serviceWorker
            .getRegistrations()
            .then(function(registrations) {
              for (let registration of registrations) {
                registration.unregister();
              }
              window.localStorage.clear();
              window.localStorage.setItem('fversion', items[0].version);
              window.location.reload();
            });
          // this.setState({
          //   changeLogs: items,
          // });
        });
    }
  };

  calcSize = () => {
    setTimeout(() => {
      if (!this.rootRef.current) {
        return;
      }
      const payload = {
        width: this.rootRef.current.clientWidth,
        height: this.rootRef.current.clientHeight,
        id: defaultComponentId,
      };
      const componentName = this.getComponentName();
      this.dispatch(componentName, payload, 'CHANGE_SIZE_COMPONENT');
    }, 0);
  };

  onBeforeUnload = () => {
    localStorage.setItem(
      CURRENT_STATE_KEY,
      JSON.stringify(currentState.getAll()),
    );
  };

  changeTitle = () => {
    setTimeout(() => {
      const listComponentTitle = I18n.t('common.listComponent');
      if (listComponentTitle[this.getComponentName()]) {
        document.title = listComponentTitle[this.getComponentName()];
      }
    }, 0);
  };

  loadCurrentState = () => {
    try {
      const currentStateString = localStorage.getItem(CURRENT_STATE_KEY);
      if (currentStateString) {
        const state = JSON.parse(currentStateString);
        currentState.apply(state || {});
      }
    } catch (e) {}
  };

  getComponentName = () => {
    const { computedMatch } = this.props;
    return mapComponentName[computedMatch.params.name] || null;
  };

  initComponent = () => {
    const componentName = this.getComponentName();
    this.dispatch(componentName, { id: defaultComponentId }, 'INIT_COMPONENT');
  };

  dispatch(componentName, payload, type) {
    widgetConfig.forEach(({ list }) => {
      list.forEach(({ listReducer, name }) => {
        if (componentName === name) {
          listReducer &&
            listReducer.forEach(item => {
              store.dispatch({
                type: `${item}${type}`,
                payload,
              });
            });
        }
      });
    });
  }

  isRender = () => {
    const {
      listOrganizationByOrganCode,
      listEconomyByEconomyCode,
      listCompanyGroupByComGroupCode,
      coveredWarrantDataByCode,
    } = this.props;
    return (
      objectLength(listOrganizationByOrganCode) &&
      objectLength(listEconomyByEconomyCode) &&
      objectLength(listCompanyGroupByComGroupCode) &&
      objectLength(coveredWarrantDataByCode)
    );
  };

  render() {
    const { computedMatch } = this.props;
    const Component =
      this.isRender() && mapComponent[computedMatch.params.name]
        ? mapComponent[computedMatch.params.name]
        : DefaultComponent;
    return (
      <div ref={this.rootRef} className="lm_root h-100 independent-component">
        <div className="h-100 lm_item_container">
          <div className="lm_items h-100">
            <div className="lm_content h-100">
              <div className="lm_item h-100">
                <Suspense fallback={null}>
                  <Component {...customProps} />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  i18n: state.i18n,
  listOrganizationByOrganCode: state[REDUCER_NAME].listOrganizationByOrganCode,
  listDerivativeByDerivativeCode:
    state[REDUCER_NAME].listDerivativeByDerivativeCode,
  listEconomyByEconomyCode: state[REDUCER_NAME].listEconomyByEconomyCode,
  listCompanyGroupByComGroupCode:
    state[REDUCER_NAME].listCompanyGroupByComGroupCode,
  coveredWarrantDataByCode: state[REDUCER_NAME].coveredWarrantDataByCode,
});
const mapDispatchToProps = { setUserInfo };

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Component);
